import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { ref, computed } from 'vue'

export const useStatisticsSettingsStore = defineStore('statisticsSettings', () => {
  // ========================================
  // State
  // ========================================
  const isLoading = ref(false)

  const settings = ref({
    content: {},
    areLoaded: false,
  })

  // Centralized generic range selection for all statistics tabs.
  const RANGE_START_KEY = 'statistics-selected-range-start'
  const RANGE_END_KEY = 'statistics-selected-range-end'

  const rangeStart = useStorage(RANGE_START_KEY, new Date().toISOString(), localStorage, {
    listenToStorageChanges: false,
  })

  const rangeEnd = useStorage(RANGE_END_KEY, new Date().toISOString(), localStorage, {
    listenToStorageChanges: false,
  })

  /**
   * Writes the range this tab currently shows back to localStorage.
   * The refs above ignore storage events, so another tab may have left a different range behind -
   * call this before opening a statistics page in a new tab, so it starts with the range on screen.
   */
  const persistDateRange = () => {
    localStorage.setItem(RANGE_START_KEY, rangeStart.value)
    localStorage.setItem(RANGE_END_KEY, rangeEnd.value)
  }


  // Shared gross/net switch for all statistics tabs, sent to backend as amountType
  const amountType = useStorage('statistics-amount-type', 'gross')

  // Shared "include unfinished visits" switch for all statistics tabs, sent to backend as includeUnfinishedEvents
  const addUnfinishedVisits = useStorage('statistics-add-unfinished-visits', true)

  const setRange = ({ start, end }) => {
    rangeStart.value = new Date(start).toISOString()
    rangeEnd.value = new Date(end).toISOString()
  }

  const dateRangeModel = computed({
    get: () => ({
      start: new Date(rangeStart.value),
      end: new Date(rangeEnd.value),
    }),
    set: value => setRange(value),
  })
  // ========================================
  // ACCESSES
  // ========================================
  // mode:
  // full — pełny dostęp, wszyscy pracownicy
  // current — wszyscy pracownicy, tylko aktualny dzień (zablokuj datepickera)
  // self — tylko własne dane (ukryj filtr po pracowniku)
  // null — tylko gdy access: false, nieistotny
  //
  // dataType:
  // aggregated — dane zagregowane per pracownik → odpytaj endpoint listowy
  // transactional — każda transakcja osobno → odpytaj endpoint szczegółowy (brak filtra po pracowniku)

  // ========================================
  // Actions
  // ========================================

  const loadSettings = async () => {
    isLoading.value = true
    try {

      const response = await $backend(`/statistics/settings/index`, {
        method: 'GET',
      })

      if (response.success) {
        console.log('loading settings...', response)
        settings.value.content = response.data
        settings.value.areLoaded = true

        return true
      }

    } catch (e) {
      settings.value.areLoaded = false

      return false
    } finally {
      isLoading.value = false
    }

  }

  /**
   * Helper to get a specific setting by key
   */
  const getSetting = (key) => settings.value.content[key] || { access: false, mode: null, dataType: null }

  /**
   * Employee Finance Permissions
   */
  const getPermissions = (settingKey) => {
    const s = getSetting(settingKey)

    return {
      hasAccess: s.access,
      isSelfMode: ['self', 'self_current'].includes(s.mode),
      isFullMode: s.mode === 'full',
      isCurrentDayOnly: s.mode === 'current',
      isTransactional: s.dataType === 'transactional',
      isAggregated: s.dataType === 'aggregated',
    }
  }


  return {
    rangeStart,
    rangeEnd,
    dateRangeModel,
    persistDateRange,
    amountType,
    addUnfinishedVisits,
    isLoading,
    settings,
    getPermissions,
    loadSettings,
    getSetting,
  }
})
